/**
 * K2 „Was aus den Anfos eines Quartals geworden ist" (P2-V3 § 2.7): Anteil je Rechnungsstufe, Grafik je Bestellmonat
 * (gestapelt von „bezahlt" an der Grundlinie bis „vor der ersten Rechnung beendet"), Tabelle je Bestellquartal mit den
 * Monaten als ▸-Zeilen (Regel Z b: Summenzeile sichtbar, der Schalter nennt die Menge). Summenzeile nur über die
 * reifen Quartale. Audit D6/D8 (30.10.2026): bezahlt in Blau, verloren in Orange, „Rechnung kommt noch" und junge Monate
 * mit `UNSICHER` (`vizStyle.rechnungsstufen`, je Gruppe `--ordinal` geprüft); Stapel = Legende. Junge Zeilen (mehr als 20 % der Anfos jünger als die Reife) stehen grau, ihr Etikett nennt den Anteil, der
 * noch keine Rechnung hat. Vorläufig ergänzte Anfos (Zahlstand unbekannt) als eigenes neutrales Segment ganz oben;
 * die Anteile der Tabelle rechnen ohne sie (Till 14.10.2026).
 * Persona-Umbau C3/C4 (28.10.2026): Befund „Größter Verlust" über die reifen Quartale, diese Spalte hervorgehoben
 * (Kopfgruppe, fett); Erwarteter Wert je Stufe als Wahlspalte; „Läuft nach 6 Monaten noch" aus der entfallenen
 * Halbjahreskarte steht hier je Quartal (dieselbe Regel `laeuftNach6MonatenSql`).
 */
import { FC, useMemo } from 'react';
import { Box, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { BarDatum, ResponsiveBar } from '@nivo/bar';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { Legende, RECHNUNGSSTUFEN_STAPEL, TipRow, achse, rechnungsstufeFarbe, unsicherFuellung, unsicherKonturen, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { BefundZeile, Etikett, Hinweis, int, JungEtikett, Laden, nachGruppenReihenfolge, tEuro, useLaden, vorlaeufigSatz } from '../../../components/auswertungen/kennzahlTeile';
import { formatPct, jungTip, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchBezahlqualitaetP2, formatMonat, formatPeriode } from '../../../helpers/auswertungenData';
import type { AbbruchTage, QualitaetZeileC } from '../../../helpers/umsatzData';
import AbbruchTageGrafik from './AbbruchTageGrafik';
import { groessterVerlust, VerlustStufe } from './befund';
import { DatenstandText, Kartenkopf, letzterTag, P2_GRUPPEN, schwellenAus } from './teile';

type QualitaetZeileP2 = QualitaetZeileC;
/** Bindung (C4): erst, wenn alle Anfos der Zeile 180 Tage alt sind (Regel der früheren Halbjahreskarte). */
const BINDUNG_TAGE = 180;

type StufeKey = 'paid' | 'trial_only' | 'invoiced_unpaid' | 'no_invoice_running' | 'no_invoice_ended';
type SegmentKey = StufeKey | 'provisional';

/** Namen aus TERMS (P2-V3 § 4.3); Reihenfolge der Tabelle. Farben und Stapel: `rechnungsstufeFarbe`/`RECHNUNGSSTUFEN_STAPEL`. */
const STUFEN: { key: StufeKey; term: { label: string; short: string; tip: string } }[] = [
  { key: 'paid', term: TERMS.stufeBezahlt },
  { key: 'trial_only', term: TERMS.stufeNurTestphase },
  { key: 'invoiced_unpaid', term: TERMS.stufeRechnungOffen },
  { key: 'no_invoice_running', term: TERMS.stufeRechnungKommt },
  { key: 'no_invoice_ended', term: TERMS.stufeVorErsterRechnung },
];
const VORLAEUFIG_LABEL = 'Zahlstand unbekannt (vorläufig)';

const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['q', 'anfos', 'paid', 'no_invoice_ended', 'invoiced_unpaid', 'trial_only', 'laeuft'],
  mittel: ['q', 'anfos', 'paid', 'no_invoice_ended', 'invoiced_unpaid', 'laeuft'],
  schmal: ['q', 'paid', 'no_invoice_ended'],
};

/** Anteil „Läuft nach 6 Monaten noch" je Quartal; „—" mit Grund, solange nicht alle Anfos 180 Tage alt sind. */
const LaeuftZelle: FC<{ r: QualitaetZeileC }> = ({ r }) => {
  const rate = r.laeuft_nach_6_monaten ?? null;
  if (rate === null)
    return (
      <Tooltip
        title={
          r.youngest_age_days == null
            ? `Noch keine ${BINDUNG_TAGE} Tage.`
            : `Erst, wenn alle Anfos der Zeile ${BINDUNG_TAGE} Tage alt sind; die jüngsten sind erst ${int(r.youngest_age_days)} Tage alt.`
        }
        arrow
        placement="top"
      >
        <Box component="span" sx={{ cursor: 'help', lineHeight: 1.25, color: 'text.secondary' }}>
          —
          <Box component="span" sx={{ display: 'block', fontSize: 11 }}>
            noch keine {BINDUNG_TAGE} Tage
          </Box>
        </Box>
      </Tooltip>
    );
  return (
    <Box sx={{ lineHeight: 1.25 }}>
      {formatPct(rate)}
      <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
        {int(r.laeuft_zaehler ?? 0)} von {int(r.abo_anfos ?? 0)}
      </Box>
    </Box>
  );
};

/** `2025-04-01` → `2025-Q2`. */
const quartalVon = (iso: string) => `${iso.slice(0, 4)}-Q${Math.floor((Number(iso.slice(5, 7)) - 1) / 3) + 1}`;
const bekannt = (r: Pick<QualitaetZeileP2, 'anfos' | 'provisional'>) => r.anfos - r.provisional;
const anteil = (n: number, von: number) => (von > 0 ? (n / von) * 100 : null);

const Anteil: FC<{ n: number; von: number; mitNenner?: boolean; fett?: boolean }> = ({ n, von, mitNenner, fett }) => (
  <Box sx={{ lineHeight: 1.25, fontWeight: fett ? 700 : undefined }}>
    {formatPct(anteil(n, von))}
    <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
      {mitNenner ? `${int(n)} von ${int(von)}` : int(n)}
    </Box>
  </Box>
);

const wertDerStufe = (r: QualitaetZeileC, k: VerlustStufe): number | null => {
  const v = r[`value_${k}` as const];
  return typeof v === 'number' ? v : null;
};

/** Erwarteter Wert der Anfos einer Stufe, darunter der Anteil am Erwarteten Wert der Zeile (C3, Wahlspalte). */
const WertStufe: FC<{ r: QualitaetZeileC; k: VerlustStufe }> = ({ r, k }) => {
  const v = wertDerStufe(r, k);
  if (v === null) return <>—</>;
  return (
    <Box sx={{ lineHeight: 1.25 }}>
      {tEuro(v)}
      {(r.value_sum ?? 0) > 0 && (
        <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
          {formatPct((v / (r.value_sum as number)) * 100, 0)} von {tEuro(r.value_sum)}
        </Box>
      )}
    </Box>
  );
};

const BezahlqualitaetKarte: FC = () => {
  const { data, fehler } = useLaden(fetchBezahlqualitaetP2);
  const viz = useVizStyle();
  const theme = useTheme();
  const breit = useMediaQuery(theme.breakpoints.up('md'));
  const s = schwellenAus(data);
  // D8 (Audit 30.10.2026): bezahlt blau, verloren orange, „Rechnung kommt noch" UNSICHER; vorläufig = Kontext grau.
  const farbe = (k: SegmentKey) => (k === 'provisional' ? viz.muted : rechnungsstufeFarbe(viz, k).farbe);
  const segUnsicher = (k: SegmentKey) => k !== 'provisional' && rechnungsstufeFarbe(viz, k).unsicher;
  const segLabel = (k: SegmentKey) => (k === 'provisional' ? VORLAEUFIG_LABEL : STUFEN.find((x) => x.key === k)?.term.label ?? k);
  const mitVorlaeufig = (data?.summary.provisional ?? 0) > 0;
  const abbruch = (data as { abbruch_tage?: AbbruchTage } | null)?.abbruch_tage ?? null;
  // Stapel und Legende in derselben Reihenfolge (von der Grundlinie nach oben).
  const segmente: SegmentKey[] = [...RECHNUNGSSTUFEN_STAPEL, ...(mitVorlaeufig ? (['provisional'] as const) : [])];
  // D6: junge Monate und die offene Stufe in ihrer Farbe mit geringer Deckkraft + Kontur.
  const istUnsicher = (k: SegmentKey, jung: boolean) => k !== 'provisional' && (jung || segUnsicher(k));

  const chartData = useMemo(
    () =>
      (data?.months ?? []).map((m) => {
        const row: Record<string, number | string | boolean> = { month: m.month, anfos: m.anfos, jung: m.is_young };
        for (const k of [...STUFEN.map((x) => x.key), 'provisional'] as SegmentKey[]) {
          row[k] = m.anfos > 0 ? (m[k] / m.anfos) * 100 : 0;
          row[`${k}_n`] = m[k];
        }
        return row;
      }),
    [data],
  );
  const ticks = useMemo(() => chartData.map((d) => String(d.month)).filter((_, i, a) => (a.length - 1 - i) % (breit ? 2 : 6) === 0), [chartData, breit]);

  // Zeilen: Quartale mit ihren Monaten als Kindzeilen (gleicher Typ).
  const zeilen: QualitaetZeileP2[] = useMemo(
    () =>
      (data?.quarters ?? [])
        .map((q) => ({
          ...q,
          key: q.quarter,
          months: q.months.map((m) => ({ ...m, key: m.month, quarter: undefined, month: m.month, months: undefined }) as QualitaetZeileP2),
        }))
        .reverse() as QualitaetZeileP2[],
    [data],
  );
  const rs = data?.reife_summe as (QualitaetZeileC & { von: string; bis: string }) | null | undefined;
  // C3: größte Verluststufe der reifen Quartale (Befund + hervorgehobene Spalte).
  const verlust = useMemo(() => groessterVerlust((data?.quarters ?? []) as unknown as Parameters<typeof groessterVerlust>[0]), [data]);
  const verlustTerm = STUFEN.find((x) => x.key === verlust.key)?.term;
  const name = (r: QualitaetZeileP2) => (r.month ? formatMonat(r.month) : formatPeriode(r.quarter ?? r.key));
  const jungGrund = (r: QualitaetZeileP2) => (r.is_young ? jungTip(s.jung_anteil, s.jung_tage) : null);

  const stufenSpalte = (k: StufeKey, extra: Partial<Spalte<QualitaetZeileP2>> = {}): Spalte<QualitaetZeileP2> => {
    const st = STUFEN.find((x) => x.key === k);
    return {
      key: k,
      label: st?.term.label ?? k,
      tip: st?.term.tip,
      gruppe: 'Rechnungsstufen',
      align: 'right',
      unsicher: jungGrund,
      sortValue: (r) => anteil(r[k], bekannt(r)),
      render: (r) => <Anteil n={r[k]} von={bekannt(r)} mitNenner={k === 'paid'} fett={k === verlust.key} />,
      summary: rs ? <Anteil n={rs[k]} von={bekannt(rs)} mitNenner={k === 'paid'} fett={k === verlust.key} /> : null,
      ...(k === verlust.key ? { kopfGruppe: TERMS.groessterVerlust.label } : {}),
      ...extra,
    };
  };

  const baue = (sichtbar: ReadonlySet<string>): Spalte<QualitaetZeileP2>[] => [
    {
      key: 'q',
      label: 'Bestellquartal',
      gruppe: 'Menge',
      pflicht: true,
      minWidth: 120,
      render: (r) => (
        <Box component="span" sx={{ lineHeight: 1.3 }}>
          {name(r)}
          {!sichtbar.has('anfos') && (
            <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
              {int(r.anfos)} Anfos
            </Box>
          )}
        </Box>
      ),
      summary: rs ? (
        <Box component="span" sx={{ lineHeight: 1.3 }}>
          {TERMS.reifeQuartale.label}
          <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400 }}>
            {formatPeriode(quartalVon(rs.von))} – {formatPeriode(quartalVon(letzterTag(rs.bis)))}
          </Box>
        </Box>
      ) : null,
    },
    {
      key: 'anfos',
      label: TERMS.anfos.label,
      tip: TERMS.anfos.tip,
      gruppe: 'Menge',
      align: 'right',
      sortValue: (r) => r.anfos,
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          {int(r.anfos)}
          {r.provisional > 0 && (
            <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
              davon {int(r.provisional)} vorläufig
            </Box>
          )}
        </Box>
      ),
      summary: rs ? int(rs.anfos) : null,
    },
    stufenSpalte('paid', { pflicht: true, einheit: TERMS.stufeBezahlt.einheit, label: TERMS.stufeBezahlt.label }),
    stufenSpalte('no_invoice_ended'),
    stufenSpalte('invoiced_unpaid'),
    stufenSpalte('trial_only'),
    stufenSpalte('no_invoice_running'),
    {
      key: 'laeuft',
      label: TERMS.laeuftNach6Monaten.label,
      tip: TERMS.laeuftNach6Monaten.tip,
      einheit: TERMS.laeuftNach6Monaten.einheit,
      gruppe: 'Preis und Bindung',
      align: 'right',
      sortValue: (r) => r.laeuft_nach_6_monaten ?? null,
      render: (r) => <LaeuftZelle r={r} />,
      summary: rs ? <LaeuftZelle r={rs} /> : null,
    },
    {
      key: 'wertVerlust',
      label: verlustTerm ? `${TERMS.wertRechnungsstufe.short}: ${verlustTerm.short}` : TERMS.wertRechnungsstufe.label,
      tip: TERMS.wertRechnungsstufe.tip,
      einheit: TERMS.wertRechnungsstufe.einheit,
      gruppe: 'Wert',
      align: 'right',
      sortValue: (r) => wertDerStufe(r, verlust.key),
      render: (r) => <WertStufe r={r} k={verlust.key} />,
      summary: rs ? <WertStufe r={rs} k={verlust.key} /> : null,
    },
    {
      key: 'widerruf',
      label: TERMS.widerrufsquote.label,
      tip: TERMS.widerrufsquote.tip,
      gruppe: 'Rechnungsstufen',
      align: 'right',
      sortValue: (r) => anteil(r.withdrawn ?? 0, r.abo_anfos ?? 0),
      render: (r) => <Anteil n={r.withdrawn ?? 0} von={r.abo_anfos ?? 0} mitNenner />,
      summary: rs ? <Anteil n={rs.withdrawn ?? 0} von={rs.abo_anfos ?? 0} mitNenner /> : null,
    },
    {
      key: 'offen',
      label: TERMS.offenerRechnungsbetrag.label,
      tip: TERMS.offenerRechnungsbetrag.tip,
      einheit: 'netto',
      gruppe: 'Zahlung',
      align: 'right',
      sortValue: (r) => r.offener_betrag ?? null,
      render: (r) => tEuro(r.offener_betrag ?? null),
      summary: rs ? tEuro(rs.offener_betrag ?? null) : null,
    },
  ];
  const meta = nachGruppenReihenfolge(spaltenMeta(baue(new Set())), P2_GRUPPEN);
  const wahl = useSpaltenwahl('umsatzRechnungsstufen', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);
  const schmal = wahl.breite === 'schmal';
  // Schmal (< 640 px) ohne Mindestbreite der Namensspalte (Abnahme 25.10.2026: 314 px Tabelle in 267 px Karte).
  const spalten = baue(sichtbar)
    .filter((c) => sichtbar.has(c.key))
    .map((c) => (c.key === 'q' && schmal ? { ...c, minWidth: undefined } : c));

  return (
    <ChartCard
      title="Was aus den Anfos eines Quartals geworden ist"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead="Je Bestellzeitraum: welcher Teil bezahlt hat (unten, kräftig) und woran es bei den übrigen lag. Graue Zeiträume sind jung, dort kommen noch Rechnungen. Die Monate eines Quartals stehen aufklappbar darunter. Ganz unten: an welchem Tag nach der Bestellung die Anfos endeten, die vor ihrer ersten Rechnung aufgehört haben."
      terms={[
        { label: TERMS.rechnungsstufe.label, tip: TERMS.rechnungsstufe.tip },
        ...STUFEN.map((x) => ({ label: x.term.label, tip: x.term.tip })),
        { label: TERMS.reifeQuartale.label, tip: TERMS.reifeQuartale.tip },
        { label: TERMS.groessterVerlust.label, tip: TERMS.groessterVerlust.tip },
        { label: TERMS.laeuftNach6Monaten.label, tip: TERMS.laeuftNach6Monaten.tip },
        { label: TERMS.wertRechnungsstufe.label, tip: TERMS.wertRechnungsstufe.tip },
        { label: TERMS.jung.label, tip: TERMS.jung.tip },
        ...(mitVorlaeufig ? [{ label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip }] : []),
        ...(abbruch
          ? [
              { label: TERMS.abbruchTag.label, tip: TERMS.abbruchTag.tip },
              { label: TERMS.abbruchSpanne.label, tip: TERMS.abbruchSpanne.tip },
              ...(abbruch.testphase_linie !== null ? [{ label: TERMS.endeTestphase.label, tip: TERMS.endeTestphase.tip }] : []),
            ]
          : []),
      ]}
      source="PL-X-Bestellungen und Zahlungen, PX-Data-Export"
      freshnessKey={['plx_orders', 'px_ergaenzung']}
    >
      <Box ref={wahl.messRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            <BefundZeile
              text={'text' in verlust.befund ? verlust.befund.text : undefined}
              fehlt={'fehlt' in verlust.befund ? verlust.befund.fehlt : null}
              tip={TERMS.wertRechnungsstufe.tip}
            />
            <Kartenkopf>
              Bestellungen ab {data.window.von.slice(8, 10)}.{data.window.von.slice(5, 7)}.{data.window.von.slice(0, 4)} bis <DatenstandText ds={data.datenstand} /> ·
              Grafik je Monat, Tabelle je Quartal
            </Kartenkopf>
            <Legende
              items={[
                ...segmente.map((k) => ({ label: segLabel(k), color: farbe(k), unsicher: segUnsicher(k) })),
                ...(chartData.some((d) => d.jung) ? [{ label: 'junger Monat (es kommen noch Rechnungen)', color: viz.inkSecondary, unsicher: true }] : []),
              ]}
            />
            <Box sx={{ height: 300 }} data-grafik="rechnungsstufen">
              <ResponsiveBar
                data={chartData as unknown as BarDatum[]}
                keys={segmente}
                indexBy="month"
                groupMode="stacked"
                margin={{ top: 8, right: 8, bottom: 32, left: 48 }}
                padding={0.3}
                valueScale={{ type: 'linear', min: 0, max: 100 }}
                colors={(b) => (istUnsicher(b.id as SegmentKey, Boolean(b.data.jung)) ? unsicherFuellung(farbe(b.id as SegmentKey)) : farbe(b.id as SegmentKey))}
                borderWidth={1}
                borderColor={theme.palette.background.default}
                theme={viz.nivo}
                animate={false}
                enableLabel={false}
                enableGridY
                gridYValues={[0, 25, 50, 75, 100]}
                axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: [0, 25, 50, 75, 100], format: achse('%') }}
                axisBottom={{ tickSize: 0, tickPadding: 6, tickValues: ticks, format: (v) => formatMonat(String(v)) }}
                tooltip={({ id, indexValue, data: d }) => {
                  const k = id as SegmentKey;
                  const n = Number(d[`${k}_n`]);
                  const von = Number(d.anfos);
                  return (
                    <Box sx={{ ...(viz.nivo.tooltip as { container: object }).container, px: 1.5, py: 1 }}>
                      <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                        {formatMonat(String(indexValue))}
                        {d.jung ? ' · jung' : ''}
                      </Box>
                      <TipRow color={farbe(k)} label={segLabel(k)} value={formatPct(anteil(n, von))} />
                      <Box sx={{ opacity: 0.8 }}>
                        {int(n)} von {int(von)} Anfos
                      </Box>
                    </Box>
                  );
                }}
                layers={[
                  'grid',
                  'axes',
                  'bars',
                  unsicherKonturen((b) => (istUnsicher(b.data.id as SegmentKey, Boolean(b.data.data.jung)) ? farbe(b.data.id as SegmentKey) : null)),
                ]}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <SortableTable
                rows={zeilen}
                columns={spalten}
                rowKey={(r) => r.key}
                kinder={(r) => (r.months && r.months.length > 1 ? r.months : undefined)}
                kinderText={(r) => `${int(r.months?.length ?? 0)} Monate`}
                maxHeight={null}
                eng={wahl.breite === 'schmal'}
                namensEtikett={(r) =>
                  r.is_young ? (
                    <>
                      <JungEtikett jungAnteil={s.jung_anteil} jungTage={s.jung_tage} />
                      {(r.no_invoice_share ?? 0) >= 1 &&
                        (schmal ? (
                          // Schmal als eigene, umbrechbare Zeile — als Etikett (nowrap) machte es die Namensspalte zu breit.
                          <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', whiteSpace: 'normal' }}>
                            {formatPct(r.no_invoice_share ?? null, 0)} noch ohne Rechnung
                          </Box>
                        ) : (
                          <Etikett>{formatPct(r.no_invoice_share ?? null, 0)} noch ohne Rechnung</Etikett>
                        ))}
                    </>
                  ) : null
                }
              />
            </Box>
            {/* Workshop-Lücken W1 (29.10.2026): Tag des Endes der vor der ersten Rechnung beendeten Anfos (Feld nur im neuen Backend). */}
            {abbruch && <AbbruchTageGrafik daten={abbruch} />}
            <Hinweis>
              Anteile über die Anfos mit bekanntem Zahlstand. „{TERMS.stufeBezahlt.label}“ heißt mehr als die 0,99-€-Testphase; zusammen mit „
              {TERMS.stufeNurTestphase.label}“, „{TERMS.stufeRechnungOffen.label}“, „{TERMS.stufeVorErsterRechnung.label}“ und „{TERMS.stufeRechnungKommt.label}“
              (im Spaltenmenü) ergibt jede Zeile 100 %. Die Rechnungsstufe kommt aus dem, was PL-X heute zur Anfo meldet; eine Rechnungshistorie
              liefert PL-X nicht. Die Summenzeile zählt nur reife Quartale. „{TERMS.laeuftNach6Monaten.label}“ erst, wenn alle Anfos der Zeile{' '}
              {BINDUNG_TAGE} Tage alt sind (früher je Halbjahr in einer eigenen Karte).
              {mitVorlaeufig &&
                ` ${vorlaeufigSatz(data.summary.provisional)} In der Grafik stehen sie grau ganz oben, damit jeder Monat 100 % ergibt; die Anteile der Tabelle rechnen ohne sie.`}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default BezahlqualitaetKarte;
